// SPDX-License-Identifier: LicenseRef-Proprietary

/**
 * AI Evidence Service
 * 
 * Purpose: API client for AI-powered abstract triage
 * Endpoints: POST /api/ai/abstractTriage
 * 
 * @note Follows existing API patterns from pubmedService.ts and decisionTreeService.ts
 */

import type { 
  AbstractTriageRequest, 
  AbstractTriageResponse,
  AbstractTriageBrief
} from '../components/VaccineDashboard/lib/models';

// ============================================================================
// Error Handling
// ============================================================================

/**
 * AI API error codes mapping to user-friendly messages
 */
const AI_ERROR_MESSAGES: Record<string, string> = {
  'EHTA-AI-001': 'AI service not configured',
  'EHTA-AI-002': 'Invalid abstract content or formatting',
  'EHTA-AI-003': 'OpenAI API error: Unable to generate summary',
  'EHTA-AI-004': 'Rate limit exceeded: Please try again in a few minutes',
  'EHTA-AI-005': 'Batch size exceeded: Maximum 10 items per request',
  'EHTA-AI-500': 'Internal server error: AI processing failed',
};

/**
 * Error object within error response
 */
export interface AiApiError {
  code: string;                   // EHTA-AI-XXX format
  message: string;
  details?: Record<string, unknown>;
}

/**
 * Full error response from AI API
 */
export interface AiErrorResponse {
  requestId: string;
  error: AiApiError;
}

/**
 * API error with structured details
 */
export class AiApiErrorClass extends Error {
  public readonly code: string;
  public readonly requestId: string;
  public readonly status: number;
  public readonly details?: Record<string, unknown>;

  constructor(errorResponse: AiErrorResponse, status: number) {
    const userMessage = AI_ERROR_MESSAGES[errorResponse.error.code] || errorResponse.error.message || 'AI service error';
    super(userMessage);
    this.name = 'AiApiError';
    this.code = errorResponse.error.code;
    this.requestId = errorResponse.requestId;
    this.status = status;
    this.details = errorResponse.error.details;
  }
}

// ============================================================================
// CSRF Token Management
// ============================================================================

let csrfToken: string | null = null;

/**
 * Ensure CSRF token is available for authenticated requests
 * @param forceRefresh - If true, bypass cache and fetch a fresh token
 */
async function ensureCsrf(forceRefresh = false): Promise<string> {
  if (csrfToken && !forceRefresh) return csrfToken;

  try {
    const res = await fetch('/api/auth/csrf', {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      signal: AbortSignal.timeout(5000),
    });

    if (!res.ok) return 'no-csrf';

    const data = await res.json();
    csrfToken = data.csrf_token;
    return csrfToken!;
  } catch (error) {
    console.warn('AI Service: CSRF endpoint not available, proceeding without CSRF protection');
    return 'no-csrf';
  }
}

/**
 * Invalidate the cached CSRF token (called on CSRF validation errors)
 */
function invalidateCsrfToken(): void {
  csrfToken = null;
}

// ============================================================================
// API Methods
// ============================================================================

/**
 * Normalize PubMed paper data into AI triage request format
 * Handles camelCase → snake_case conversion and ensures arrays are always defined
 * Robustly handles both string and array inputs (jsonlite parse variations)
 */
export function normalizePaperForTriage(paper: {
  pmid: string;
  title: string;
  abstract?: string;
  year?: number;
  journal?: string;
  publicationTypes?: string[] | string;
  keywords?: string[] | string;
  meshTerms?: string[] | string;
}): any {
  // Helper to ensure array, handles both string and array inputs
  const ensureArray = (val: any): string[] => {
    if (val == null) return [];
    if (Array.isArray(val)) return val.filter(x => typeof x === 'string' && x.trim().length > 0);
    if (typeof val === 'string') return val.trim().length > 0 ? [val] : [];
    return [];
  };

  return {
    pmid: paper.pmid,
    title: paper.title,
    abstract: paper.abstract || "",
    year: paper.year,
    journal: paper.journal,
    pub_types: ensureArray(paper.publicationTypes),
    keywords: ensureArray(paper.keywords),
    mesh_terms: ensureArray(paper.meshTerms)
  };
}

/**
 * Handle common error cases for AI API calls
 */
function handleAiError(error: unknown, timeoutMessage: string): never {
  if (error instanceof AiApiErrorClass) {
    throw error;
  }

  if (error instanceof DOMException && error.name === 'AbortError') {
    throw error;
  }

  if (error instanceof TypeError && error.message.includes('fetch')) {
    throw new Error('Network error: Unable to reach AI triage service');
  }

  if (error instanceof DOMException && error.name === 'TimeoutError') {
    throw new Error(timeoutMessage);
  }

  throw error;
}

/**
 * Request AI-powered abstract triage for one or more PubMed papers
 * 
 * @param request - Triage parameters (items with PMID, title, abstract)
 * @param signal - AbortSignal for request cancellation
 * @returns Triage results with hta_brief summaries
 * @throws AiApiErrorClass with structured error details
 * 
 * @example
 * ```typescript
 * const results = await aiAbstractTriage({
 *   mode: "hta_brief",
 *   items: [{ pmid: "123", title: "...", abstract: "..." }]
 * });
 * ```
 */
export async function aiAbstractTriage(
  request: AbstractTriageRequest,
  signal?: AbortSignal,
  isRetry = false
): Promise<AbstractTriageResponse> {
  const timeoutMs = 60000; // AI can be slow, 60s timeout

  try {
    const csrf = await ensureCsrf(isRetry);
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-Client-Request-Id': crypto.randomUUID?.() ?? String(Date.now()),
    };

    if (csrf && csrf !== 'no-csrf') {
      headers['X-CSRF-Token'] = csrf;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    if (signal) {
      signal.addEventListener('abort', () => {
        clearTimeout(timeoutId);
        controller.abort();
      });
    }

    const response = await fetch('/api/ai/abstractTriage', {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(request),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    const contentType = response.headers.get('content-type') || '';

    if (!response.ok) {
      let errorData: AiErrorResponse | null = null;
      if (contentType.includes('application/json')) {
        try {
          errorData = await response.json();
        } catch { /* Ignore parse error */ }
      }

      if (errorData) {
        // Check for CSRF validation error - retry once with fresh token
        if (errorData.error.code === 'EHTA-CSRF-001' && !isRetry) {
          console.log('CSRF token expired, refreshing and retrying...');
          invalidateCsrfToken();
          return aiAbstractTriage(request, signal, true);
        }

        throw new AiApiErrorClass(errorData, response.status);
      }
      throw new Error(`AI Service Error: HTTP ${response.status}`);
    }

    if (!contentType.includes('application/json')) {
      throw new Error('Unexpected response format from AI service');
    }

    const data: AbstractTriageResponse = await response.json();
    
    // Response validation
    if (!data.data || !Array.isArray(data.data.items)) {
      throw new Error('Invalid response format: missing data.items array');
    }

    // Normalize items to ensure all fields exist
    const normalizedItems = data.data.items.map(item => ({
      ...item,
      brief: {
        ...item.brief,
        potentialInputSources: item.brief.potentialInputSources || [],
        cautions: item.brief.cautions || [],
      } as AbstractTriageBrief
    }));

    return { ...data, data: { items: normalizedItems } };

  } catch (error) {
    return handleAiError(error, 'AI triage request timed out (60s)');
  }
}
